import {useCallback, useEffect, useRef, useState} from 'react';
import {
    applyWikidataEntityLink,
    buildWikidataEntityMarkAttrs,
    fetchWikidataEntity,
    updateWikidataEntityMarkByUid,
} from '../helpers/wikidataHelpers';
import {useReportStore} from '../../../features/products/useReportStore';
import {resolveEntityAvatarUrl} from '../../../utils/entityAvatarUrl';
import {isLocalEntityRef} from '../../../utils/entityLinks';
import {syncKbEntityFromWikidata} from '../../../utils/syncKbEntityFromWikidata';
import {fetchEntityTypesByQid} from '../../../utils/api/entities';
import {coalesceEntityType} from '../../../utils/entityTypeMeta';

/**
 * Focus editor without scrolling, preserving the caret position.
 * @param {Object} view - The editor view
 */
const focusEditorWithoutScroll = (view) => {
    if (!view?.dom) return;
    const scrollEl = document.scrollingElement;
    const winX = window.scrollX;
    const winY = window.scrollY;
    const docScrollTop = scrollEl?.scrollTop;
    const docScrollLeft = scrollEl?.scrollLeft;

    try {
        view.dom.focus({preventScroll: true});
    } catch (error) {
        view.dom.focus();
    }

    // Ensure ProseMirror's editable root owns focus/selection state.
    view.focus();

    // Focus can still scroll in some browsers; restore on next frame.
    requestAnimationFrame(() => {
        window.scrollTo(winX, winY);
        if (scrollEl && typeof docScrollTop === 'number') {
            scrollEl.scrollTop = docScrollTop;
            if (typeof docScrollLeft === 'number') {
                scrollEl.scrollLeft = docScrollLeft;
            }
        }
    });
};

export function useWikidataEntity() {
    const setActiveWikidataEntity = useReportStore(s => s.setActiveWikidataEntity);
    const clearActiveWikidataEntity = useReportStore(s => s.clearActiveWikidataEntity);

    const [searchDialog, setSearchDialog] = useState({
        open: false,
        initialQuery: '',
        view: null,
        selectionFrom: null,
        selectionTo: null,
    });
    const searchContextRef = useRef({
        view: null,
        selectionFrom: null,
        selectionTo: null,
    });

    // Clear entity dialog on unmount
    useEffect(() => clearActiveWikidataEntity, [clearActiveWikidataEntity]);

    const openEntitySearch = useCallback((view, initialQuery = '') => {
        const currentSelection = view?.state?.selection;
        searchContextRef.current = {
            view,
            selectionFrom: typeof currentSelection?.from === 'number' ? currentSelection.from : null,
            selectionTo: typeof currentSelection?.to === 'number' ? currentSelection.to : null,
        };
        setSearchDialog({
            open: true,
            initialQuery,
            view,
            selectionFrom: searchContextRef.current.selectionFrom,
            selectionTo: searchContextRef.current.selectionTo,
        });
    }, []);

    const closeEntitySearch = useCallback((event, reason) => {
        const view = searchContextRef.current.view;
        setSearchDialog(prev => ({...prev, open: false, initialQuery: ''}));

        if (reason === 'escapeKeyDown' && view) {
            requestAnimationFrame(() => focusEditorWithoutScroll(view));
        }
    }, []);

    const handleEntitySelect = useCallback(async (entity) => {
        const view = searchContextRef.current.view || searchDialog.view;
        if (!view) return;

        const {selectionFrom, selectionTo} = searchContextRef.current;
        const {applied, uid: linkUid} = applyWikidataEntityLink(view, entity, {
            from: selectionFrom,
            to: selectionTo,
        });
        if (!applied) return;

        focusEditorWithoutScroll(view);

        if (!entity?.id || !linkUid) return;

        if (isLocalEntityRef(entity.id) || entity.local) {
            return;
        }

        const fullEntity = await fetchWikidataEntity(entity.id);
        if (!fullEntity) return;

        syncKbEntityFromWikidata(entity);

        const optimisticAttrs = buildWikidataEntityMarkAttrs(entity);
        let kbEntityType = null;
        try {
            const types = await fetchEntityTypesByQid([entity.id]);
            kbEntityType = types[entity.id.trim().toUpperCase()] || null;
        } catch (_) {
            // Best-effort — popover hydration can still backfill later.
        }
        const patch = {
            description: fullEntity.description || optimisticAttrs.description || null,
            imageUrl: resolveEntityAvatarUrl({
                wikidataId: entity.id,
                imageUrl: fullEntity.imageUrl || optimisticAttrs.imageUrl || null,
            }),
            url: optimisticAttrs.url || (entity.id ? `https://www.wikidata.org/wiki/${entity.id}` : null),
            label: optimisticAttrs.label || fullEntity.label || null,
            entityType: coalesceEntityType(
                optimisticAttrs.entityType,
                entity.entityType,
                kbEntityType,
                fullEntity.entityType,
            ),
        };

        const changed = updateWikidataEntityMarkByUid(view, linkUid, patch, {addToHistory: false});
        if (!changed) return;

        const currentActive = useReportStore.getState().activeWikidataEntity;
        if (currentActive?.uid === linkUid) {
            setActiveWikidataEntity({...currentActive, ...patch});
        }
    }, [searchDialog.view, setActiveWikidataEntity]);

    return {
        searchDialog,
        openEntitySearch,
        closeEntitySearch,
        handleEntitySelect,
    };
}
